import { focusEffectSchema, type Action, type FocusEffect, type FocusMode, type Point, type Rect } from "@veyso/core";

export interface FocusOptions {
  /** Applied to any action the user has not overridden. */
  defaultMode: FocusMode;
  /** Whether a seeded zoom also moves the pointer. */
  defaultCursor: boolean;
  defaultZoom: number;
  defaultDuration: number;
  /** Seconds the effect begins before the action, so the cursor can travel in. */
  leadIn: number;
}

export const defaultFocusOptions: FocusOptions = {
  defaultMode: "zoom",
  /** Detected clicks are worth a cursor move; it is what makes them read. */
  defaultCursor: true,
  defaultZoom: 1.35,
  // Long enough that half a second of ease at each end still leaves a hold.
  defaultDuration: 2.2,
  leadIn: 0.6,
};

const centreOf = (rect: Rect): Point => ({ x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 });

/** Where the cursor rests before the first action: bottom-centre, off the content. */
const RESTING_CURSOR: Point = { x: 0.5, y: 0.85 };

/**
 * Turns detected actions into the visual effects the renderer will draw.
 *
 * Only actions with a target rectangle produce an effect — an action detected
 * from a scene change has no idea *where* on screen the user acted, and zooming
 * to a guessed rectangle looks worse than not zooming at all. Those actions stay
 * on the timeline so the editor can offer "place the focus box here".
 *
 * Effects are clamped to the section and trimmed so they never overlap: two
 * zooms fighting over the same frames is the one thing that looks broken rather
 * than merely unpolished.
 */
export function planFocusEffects(
  actions: Action[],
  bodyDuration: number,
  options: Partial<FocusOptions> = {},
): FocusEffect[] {
  const opts = { ...defaultFocusOptions, ...options };

  const ordered = [...actions].sort((a, b) => a.timestamp - b.timestamp);
  const effects: FocusEffect[] = [];
  let cursorFrom: Point = RESTING_CURSOR;

  for (const action of ordered) {
    if (!action.target) continue;
    if (action.timestamp >= bodyDuration) break;

    const start = Math.max(0, action.timestamp - opts.leadIn);
    const available = bodyDuration - start;
    if (available <= 0.2) continue;

    const duration = Math.min(opts.defaultDuration, available);
    const candidate = focusEffectSchema.safeParse({
      id: `focus-${action.id}`,
      actionId: action.id,
      mode: opts.defaultMode,
      cursor: opts.defaultCursor,
      dim: 0,
      start: Number(start.toFixed(3)),
      duration: Number(duration.toFixed(3)),
      target: action.target,
      zoom: opts.defaultZoom,
      easing: "ease-in-out",
      cursorFrom,
      click: action.type === "click",
      label: opts.defaultMode === "callout" ? (action.label ?? "Here") : action.label,
    });
    if (!candidate.success) continue;

    const previous = effects[effects.length - 1];
    if (previous && candidate.data.start < previous.start + previous.duration) {
      const trimmed = Number((candidate.data.start - previous.start).toFixed(3));
      // Drop the earlier effect entirely rather than leave a sub-frame flash.
      if (trimmed < 0.3) {
        effects.pop();
      } else {
        previous.duration = trimmed;
      }
    }

    effects.push(candidate.data);
    cursorFrom = centreOf(action.target);
  }

  return effects;
}

/**
 * Carries the effects a timeline already has into a rebuild.
 *
 * Effects used to be recomputed from the detected actions every time, with four
 * fields — mode, zoom, easing, duration — copied over the top. Everything else
 * was regenerated, which had three consequences nobody chose: an effect could
 * not be moved in time, an effect could not exist without an action, and a
 * deleted effect came back on the next build.
 *
 * So they are authored now. `planFocusEffects` seeds a timeline that does not
 * have one yet; after that this is the only thing that touches the array, and
 * it does the one job a rebuild genuinely requires — keeping every effect
 * inside a body whose length may have changed underneath it.
 *
 * Re-running detection deliberately does not add to the layer. Once it has been
 * edited it belongs to the person who edited it, and a newly detected action
 * arrives as a marker they can promote rather than as an effect that appeared
 * on its own.
 */
export function carryFocusEffects(existing: FocusEffect[], bodyDuration: number): FocusEffect[] {
  const duration = Math.max(0, bodyDuration);

  return [...existing]
    .sort((a, b) => a.start - b.start)
    .flatMap((effect) => {
      if (effect.start >= duration) return [];
      // Auto-cut can shorten the body under an effect. Trimming it is right;
      // leaving a hundredth of a second of zoom behind is a flash frame.
      const available = duration - effect.start;
      if (available < MIN_EFFECT_SECONDS) return [];
      return [{ ...effect, duration: Number(Math.min(effect.duration, available).toFixed(3)) }];
    });
}

/** Below this an effect is a flicker rather than a shot. */
const MIN_EFFECT_SECONDS = 0.2;
